<script setup lang="ts">
import { computed, onMounted, ref, watch, type Component } from 'vue'
import { useI18n } from 'vue-i18n'
import { useToast } from 'primevue/usetoast'
import { storeToRefs } from 'pinia'
import Menu from 'primevue/menu'
import Dialog from 'primevue/dialog'
import type { MenuItem } from 'primevue/menuitem'

import AppButton from '@/components/atoms/buttons/AppButton.vue'
import AppDarkMode from '@/components/molecules/dark-mode/AppDarkMode.vue'
import AppThemeSwitcher from '@/components/molecules/theme/AppThemeSwitcher.vue'
import AppOptionSwitcher from '../notes/AppOptionSwitcher.vue'
import { useCampaignRegistrationSync } from '@/composables/useCampaignRegistrationSync'
import { useWebphonePermissions } from '@/composables/useWebphonePermissions'
import { useAuthStore } from '@/stores/useAuthStore'
import { useConnectivityStore } from '@/stores/useConnectivityStore'
import { useWorkspaceStore } from '@/stores/useWorkspaceStore'
import { usePauseStatusStore } from '@/stores/usePauseStatusStore'
import type { ActivePauseSelection } from '@/stores/usePauseStatusStore'
import { usePauseCreateConfirmDialog } from '@/composables/usePauseCreateConfirmDialog'
import { useCampaignRegistrationStore } from '@/stores/useCampaignRegistrationStore'
import { useCallService } from '@/services/call/useCallService'
import { useCampaignService } from '@/services/campaign/useCampaignService'
import { useChatService } from '@/services/chat/useChatService'
import { usePauseService } from '@/services/pause/usePauseService'
import { useLogoutConfirm } from '@/components/atoms/sidebars/composables/useLogoutConfirm'
import { TypeCampaign } from '@/services/campaign/enums/type-campaign.enum'
import IconInfo from '@/assets/svg/IconInfo.svg'
import IconPhone from '@/assets/svg/icon-phone.svg'
import IconIncomingArrowCircle from '@/assets/svg/iconIncomingArrowCircle.svg'
import IconExitArrowCircle from '@/assets/svg/iconExitArrowCircle.svg'
import AppNotificationSettings from '@/components/molecules/notifications/AppNotificationSettings.vue'
import IconWaitingTime from '@/assets/svg/iconWaitingTime.svg'
import type { ButtonActions } from './interface/buttonActions'
import type {
  IOperatorSessionCall,
  IOperatorSessionCallUnregister,
  IRegisterOperatorSession,
  IRegisterQueues,
  IRegisterWhatsapp,
  IUnregisterOperatorSession,
  IUnregisterWhatsapp,
  OperatorSessionChannel,
} from '@/services/call/interfaces/IRegisterQueues'
import type { IOptionsCampaign } from '@/services/campaign/interfaces/options-campaign.interface'
import type { PauseDto } from '@/services/pause/interfaces/pause.interface'
import { LogOut, WifiOff } from 'lucide-vue-next'

type CampaignExitMenuItem = MenuItem & {
  iconComponent: Component | string
}

const authStore = useAuthStore()
const connectivityStore = useConnectivityStore()
const workspace = useWorkspaceStore()
const pauseStatusStore = usePauseStatusStore()
const { confirmPauseCreation } = usePauseCreateConfirmDialog()
const campaignRegistrationStore = useCampaignRegistrationStore()
const callService = useCallService()
const campaignService = useCampaignService()
const chatService = useChatService()
const pauseService = usePauseService()
const {
  refreshChatRegistration,
  isVoiceRegistrationLost,
  isChatRegistrationLost,
  confirmCampaignAssignmentChange,
} = useCampaignRegistrationSync()
const { acceptLogout, isLogoutLoading } = useLogoutConfirm()
const toast = useToast()
const { t, locale } = useI18n()

const { shouldBlockWebphone, checkPermissions, ensureMicrophoneAccess, tooltipKey } =
  useWebphonePermissions()
const { isPaused, selectedPause, isSupervisorPause } = storeToRefs(pauseStatusStore)
const {
  isRegisteredInCampaign,
  isVoiceStateKnown,
  isVoiceRegistrationPending,
  isChatStatusLoading,
  isClassificationFormActive,
  canProcessScheduledCampaignExit,
  campaignAssignmentChanged,
} = storeToRefs(campaignRegistrationStore)
const pauseMenu = ref<InstanceType<typeof Menu> | null>(null)
const campaignExitMenu = ref<InstanceType<typeof Menu> | null>(null)
const pauses = ref<PauseDto[]>([])
const loadingAction = ref<'register' | 'unregister' | 'pause' | null>(null)
const confirmExitPauseVisible = ref(false)
const confirmScheduledCampaignExitVisible = ref(false)
const confirmExitPauseSource = ref<'pause' | 'campaign'>('pause')
const shouldLogoutAfterScheduledCampaignExit = ref(false)
const isCampaignAlertDismissed = ref(false)
const isVoiceRegistrationAlertDismissed = ref(false)
const isChatRegistrationAlertDismissed = ref(false)

watch(isRegisteredInCampaign, (val) => {
  if (val) isCampaignAlertDismissed.value = false
})

watch(isVoiceRegistrationLost, (lost) => {
  if (!lost) isVoiceRegistrationAlertDismissed.value = false
})

watch(isChatRegistrationLost, (lost) => {
  if (!lost) isChatRegistrationAlertDismissed.value = false
})

const extensionNumber = computed(() => authStore.extension?.number || '')
const extensionDescription = computed(() => authStore.extension?.description || '-')
// authStore.userExtension refleja la extensión efectiva de la sesión (propia o
// elegida en el paso 2 del login); vacía si el usuario entró sin extensión.
// Un usuario puede abrir el webphone aunque no tenga campañas de voz asignadas;
// la extensión efectiva de la sesión sigue siendo la condición necesaria.
const hasAssignedExtension = computed(() => Boolean(authStore.userExtension))
const isExtensionUnavailable = computed(
  () =>
    !authStore.webphoneAvailable ||
    !hasAssignedExtension.value ||
    authStore.extensionIsActive,
)
const isWebphoneDisconnected = computed(() => connectivityStore.isAnyServiceDisconnected)
const isWebphoneInteractionDisabled = computed(() => isExtensionUnavailable.value)
const shouldShowWebphoneDisconnectedStyle = computed(
  () =>
    isWebphoneInteractionDisabled.value ||
    isWebphoneDisconnected.value ||
    shouldBlockWebphone.value,
)

const webphoneTooltip = computed(() => t(tooltipKey.value))
const shouldShowWebphoneInfo = computed(
  () => shouldBlockWebphone.value || isWebphoneDisconnected.value,
)
const webphoneInfoTooltip = computed(() =>
  shouldBlockWebphone.value ? webphoneTooltip.value : t('header.webphone_no_connection'),
)

const webphoneButtonTooltip = computed(() => {
  // if (isWebphoneDisconnected.value) return t('header.webphone_no_connection')
  if (authStore.extensionIsActive) return t('header.webphone_extension_active')
  if (!hasAssignedExtension.value) return t('header.webphone_no_extension_assigned')
  return `${t('header.extension')}: ${extensionNumber.value}`
})

const extensionStatusLabel = computed(() =>
  isExtensionUnavailable.value
    ? t('header.extension_status.disconnected')
    : t('header.extension_status.connected'),
)

// const extensionNameWithStatus = computed(
//   () => `${extensionDescription.value} - ${extensionStatusLabel.value}`,
// )

const extensionNameWithStatus = computed(() => `${extensionDescription.value}`)

const activeWebphoneStatuses = new Set([
  'incoming',
  'dialing',
  'ringing',
  'calling',
  'connected',
  'held',
])
const hasActiveWebphoneCall = computed(() =>
  activeWebphoneStatuses.has(workspace.webphoneCallStatus),
)

const operatorCampaigns = computed<IOptionsCampaign[]>(() => {
  return workspace.campaigns
})

const hasVoiceCampaign = computed(() => workspace.hasVoiceCampaign)

// "Cargando" mientras ningún canal tiene todavía un estado confirmado por su
// fuente: la consulta de chat en vuelo, la ventana entre el POST de registro de
// voz y el primer evento de Asterisk, o el arranque sin QueueMemberGrouped aún.
// Solo se espera al evento si puede llegar: sin campaña de voz o sin extensión
// el agente no es miembro de ninguna cola y Asterisk no lo va a reportar nunca.
const isCampaignStatusLoading = computed(
  () =>
    isChatStatusLoading.value ||
    isVoiceRegistrationPending.value ||
    (hasVoiceCampaign.value && hasAssignedExtension.value && !isVoiceStateKnown.value),
)

// Sin extensión asignada, el usuario no puede atender campañas de llamadas: se
// excluyen del registro para que "Iniciar campaña" solo registre WhatsApp
// (regla de negocio de extensiones — ver getRegisterQueuePayload/enterCampaign).
const queueCampaigns = computed(() => {
  if (!hasAssignedExtension.value) return []

  return operatorCampaigns.value
    .filter((campaign) => campaign.type !== TypeCampaign.CHAT)
    .filter((campaign) => Boolean(campaign.id))
    .map((campaign) => ({
      id: campaign.id,
      queue: campaign.queue ?? '',
    }))
})

const whatsappCampaignIds = computed(() =>
  operatorCampaigns.value
    .filter((campaign) => campaign.type === TypeCampaign.CHAT)
    .map((campaign) => campaign.id)
    .filter(Boolean),
)

const campaignStatusTitle = computed(() =>
  isRegisteredInCampaign.value
    ? t('call.registered_in_campaign')
    : t('call.not_registered_in_campaign'),
)

const campaignStatusDescription = computed(() =>
  isRegisteredInCampaign.value ? t('call.ready_to_receive') : t('call.register_to_start'),
)

const campaignStatusRegisterText = computed(() => {
  const description = campaignStatusDescription.value
  const registerMatch = description.match(/registrarte|register/i)

  if (!registerMatch || registerMatch.index === undefined) {
    return {
      before: description,
      link: '',
      after: '',
    }
  }

  return {
    before: description.slice(0, registerMatch.index),
    link: registerMatch[0],
    after: description.slice(registerMatch.index + registerMatch[0].length),
  }
})

const currentPauseLabel = computed(() => {
  if (!isPaused.value) return t('header.actions.pause')
  return pauseStatusStore.formattedPauseSummary || t('call.active_pause')
})

const currentDate = computed(() => {
  const currentLocale = locale.value === 'en' ? 'en-US' : 'es-ES'

  return new Intl.DateTimeFormat(currentLocale, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date())
})

const registeredCampaignsCount = computed(() => operatorCampaigns.value.length)

const campaignRegistrationState = computed(() => {
  if (campaignAssignmentChanged.value) {
    return {
      type: 'outdated',
      label: t('header.campaign_status.outdated'),
      badgeClass:
        'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30',
      dotClass: 'bg-amber-500',
    }
  }

  if (isRegisteredInCampaign.value) {
    const count = registeredCampaignsCount.value
    const label =
      count > 0
        ? t('header.campaign_status.registered_count', { count })
        : t('header.campaign_status.registered')
    return {
      type: 'registered',
      count: count > 0 ? count : null,
      label,
      badgeClass:
        'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
      dotClass: 'bg-emerald-500',
    }
  }

  return {
    type: 'not_registered',
    label: t('header.campaign_status.not_registered'),
    badgeClass:
      'bg-[var(--p-surface-200)]/60 dark:bg-[var(--p-surface-800)]/40 text-[var(--p-surface-600)] dark:text-[var(--p-surface-300)] border-[var(--p-surface-300)]/60 dark:border-[var(--p-surface-700)]',
    dotClass: 'bg-[var(--p-surface-400)]',
  }
})

const agentOperationalState = computed(() => {
  if (connectivityStore.isAnyServiceDisconnected) {
    return {
      type: 'disconnected',
      label: t('header.operator_status.disconnected'),
      badgeClass:
        'bg-[var(--p-surface-200)]/60 dark:bg-[var(--p-surface-800)]/40 text-[var(--p-surface-600)] dark:text-[var(--p-surface-300)] border-[var(--p-surface-300)]/60 dark:border-[var(--p-surface-700)]',
      dotClass: 'bg-[var(--p-surface-400)]',
    }
  }
  if (workspace.webphoneCallStatus === 'incoming') {
    return {
      type: 'incoming_call',
      label: `${t('contact_info.incoming_call')} · ${workspace.webphoneCallDuration || '00:00'}`,
      badgeClass:
        'bg-amber-500/15 text-amber-800 dark:text-amber-200 border-amber-500/30',
      dotClass: 'bg-amber-500',
    }
  }
  if (workspace.webphoneCallStatus === 'dialing') {
    return {
      type: 'dialing',
      label: t('contact_info.dialing'),
      badgeClass:
        'bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30',
      dotClass: 'bg-blue-500',
    }
  }
  if (workspace.webphoneCallStatus === 'ringing') {
    return {
      type: 'ringing',
      label: `${t('contact_info.ringing')} · ${workspace.webphoneCallDuration || '00:00'}`,
      badgeClass:
        'bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30',
      dotClass: 'bg-blue-500',
    }
  }
  if (workspace.webphoneCallStatus === 'calling') {
    return {
      type: 'calling',
      label: `${t('contact_info.calling')} · ${workspace.webphoneCallDuration || '00:00'}`,
      badgeClass:
        'bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30',
      dotClass: 'bg-blue-500',
    }
  }
  if (workspace.webphoneCallStatus === 'connected' || workspace.webphoneCallStatus === 'held') {
    return {
      type: 'call',
      label: `${t('header.operator_status.on_call')} · ${workspace.webphoneCallDuration || '00:00'}`,
      badgeClass:
        'bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30',
      dotClass: 'bg-rose-500',
    }
  }
  if (isPaused.value) {
    return {
      type: 'pause',
      label: pauseStatusStore.formattedPauseSummary || t('call.active_pause'),
      badgeClass:
        'bg-amber-500/15 text-amber-800 dark:text-amber-200 border-amber-500/30',
      dotClass: 'bg-amber-500',
    }
  }
  return {
    type: 'available',
    label: t('header.operator_status.available'),
    badgeClass:
      'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
    dotClass: 'bg-emerald-500',
  }
})

const handleWebphoneClick = () => {
  if (isWebphoneInteractionDisabled.value) return

  if (isWebphoneDisconnected.value) {
    if (authStore.showWebphone) {
      authStore.setShowWebphone(false)
    }
    return
  }

  if (hasActiveWebphoneCall.value) {
    authStore.setShowWebphone(true)
    workspace.toggleWebphonePipExpanded()
    return
  }

  authStore.toggleWebphone()
}

const refreshWebphonePermissions = () => {
  void checkPermissions()
}

const showHeaderToast = (severity: 'success' | 'info' | 'warn' | 'error', detail: string) => {
  toast.add({
    severity,
    summary: t(
      severity === 'success'
        ? 'general.success'
        : severity === 'error'
          ? 'general.error'
          : 'general.warning',
    ),
    detail,
    life: 3000,
  })
}

const refreshWhatsAppChatsAfterCampaignRegister = async () => {
  if (!authStore.userId || !workspace.hasWhatsAppCampaign) return

  try {
    const chatsResponse = await chatService.getChats({
      user_id: authStore.userId,
    })

    window.dispatchEvent(
      new CustomEvent('whatsapp-campaign-registered', {
        detail: { chatsResponse },
      }),
    )
  } catch {}
}

const loadCampaigns = async () => {
  if (workspace.hasLoadedCampaigns) return

  const campaigns = await campaignService.getAllCampaigns()
  workspace.setCampaigns(Array.isArray(campaigns) ? campaigns : [])
}

const getSessionIdentifier = () =>
  localStorage.getItem('oper_session_identifier') ||
  authStore.currentUser?.email ||
  authStore.userAlias ||
  ''

const notifyWhatsAppOnlyRegistration = () => {
  showHeaderToast('info', t('call.whatsapp_only_registration'))
}

const validateVoiceCampaignPrerequisites = async (canRegisterWhatsapp: boolean) => {
  if (!hasVoiceCampaign.value) return true

  if (!hasAssignedExtension.value || !authStore.webphoneAvailable) {
    if (canRegisterWhatsapp) {
      notifyWhatsAppOnlyRegistration()
      return false
    }

    showHeaderToast('warn', t('call.extension_session_required'))
    return false
  }

  try {
    const hasActiveSession = await authStore.validateActiveSession(getSessionIdentifier(), {
      force: true,
    })

    if (!hasActiveSession) {
      if (canRegisterWhatsapp) {
        notifyWhatsAppOnlyRegistration()
        return false
      }

      showHeaderToast('warn', t('call.extension_session_required'))
      return false
    }
  } catch {
    if (canRegisterWhatsapp) {
      notifyWhatsAppOnlyRegistration()
      return false
    }

    showHeaderToast('error', t('auth.session_check_error'))
    return false
  }

  const microphoneAccess = await ensureMicrophoneAccess()
  if (microphoneAccess.allowed) return true

  if (canRegisterWhatsapp) {
    notifyWhatsAppOnlyRegistration()
    return false
  }

  const messageKey =
    microphoneAccess.status === 'denied'
      ? 'call.microphone_access_required'
      : 'call.microphone_device_required'

  showHeaderToast('warn', t(messageKey))
  return false
}

const loadPauses = async () => {
  const result = await pauseService.getPauses({ page: 1, size: 100 })
  pauses.value = result.data ?? []
  pauseStatusStore.hydrateFromCatalog(pauses.value)
}

const getRegisterQueuePayload = (): IRegisterQueues | null => {
  if (!authStore.userId) {
    showHeaderToast('warn', t('call.missing_user'))
    return null
  }

  return {
    user_id: authStore.userId,
    protocol: 'PJSIP',
    extension: authStore.userExtension || extensionNumber.value,
    alias: authStore.userAlias || authStore.userName || extensionDescription.value,
    campaign_ids: queueCampaigns.value,
  }
}

const getOperatorSessionCallPayload = (): IOperatorSessionCall | null => {
  const queuePayload = getRegisterQueuePayload()
  if (!queuePayload) return null

  return {
    protocol: queuePayload.protocol,
    extension: queuePayload.extension,
    alias: queuePayload.alias,
    campaign_ids: queuePayload.campaign_ids,
  }
}

const getOperatorSessionCallUnregisterPayload = (): IOperatorSessionCallUnregister | null => {
  const queuePayload = getRegisterQueuePayload()
  if (!queuePayload) return null

  return {
    protocol: queuePayload.protocol,
    extension: queuePayload.extension,
    campaign_ids: queuePayload.campaign_ids,
  }
}

const getRegisterWhatsappPayload = (): IRegisterWhatsapp | null => {
  if (!authStore.userId) {
    showHeaderToast('warn', t('call.missing_user'))
    return null
  }

  if (!whatsappCampaignIds.value.length) {
    return null
  }

  return {
    user_id: authStore.userId,
    campaign_ids: whatsappCampaignIds.value,
  }
}

const getUnregisterWhatsappPayload = (): IUnregisterWhatsapp | null => {
  if (!authStore.userId) {
    showHeaderToast('warn', t('call.missing_user'))
    return null
  }

  if (!whatsappCampaignIds.value.length) {
    return null
  }

  return {
    user_id: authStore.userId,
  }
}

const getRegisteredCampaignIds = (
  callPayload: IOperatorSessionCall | IOperatorSessionCallUnregister | null,
  whatsappCampaignIds: string[] = [],
) => {
  return Array.from(
    new Set([
      ...(callPayload?.campaign_ids.map((campaign) => campaign.id) ?? []),
      ...whatsappCampaignIds,
    ]),
  )
}

const syncRegisteredCampaignUseIa = (campaignIds: string[]) => {
  workspace.setUseIa(campaignIds.some((campaignId) => workspace.getCampaignUseIa(campaignId)))
}

const clearRegisteredCampaignUseIa = (campaignIds: string[]) => {
  campaignIds.forEach((campaignId) => {
    workspace.setCampaignUseIa(campaignId, false)
  })
  workspace.setUseIa(false)
}

const enterCampaign = async () => {
  if (!authStore.userId) {
    showHeaderToast('warn', t('call.missing_user'))
    return
  }

  const whatsappPayload = getRegisterWhatsappPayload()
  const canRegisterWhatsapp = Boolean(whatsappPayload)
  const canRegisterVoice = await validateVoiceCampaignPrerequisites(canRegisterWhatsapp)
  const callPayload =
    canRegisterVoice && queueCampaigns.value.length ? getOperatorSessionCallPayload() : null

  // Si la telefonia no puede registrarse pero existe WhatsApp, el payload sale solo con WhatsApp.
  // Si no hay ningun canal disponible, se bloquea antes de llamar al backend.
  if (!callPayload && !whatsappPayload) {
    showHeaderToast('warn', t('call.no_campaign_channels_available'))
    return
  }

  loadingAction.value = 'register'
  try {
    const payload: IRegisterOperatorSession = {
      user_id: authStore.userId,
      ...(callPayload ? { call: callPayload } : {}),
      ...(whatsappPayload ? { whatsapp: { campaign_ids: whatsappPayload.campaign_ids } } : {}),
    }

    const result = await callService.registerOperatorSession(payload, { notify: false })
    const failedChannels = result.failed_channels ?? []

    syncRegisteredCampaignUseIa(
      getRegisteredCampaignIds(callPayload, whatsappPayload?.campaign_ids ?? []),
    )

    // El registro de voz no se da por hecho acá: se marca como pendiente y lo
    // confirma el primer QueueMemberGrouped que publique el poll de colas.
    if (callPayload && !failedChannels.includes('CALL')) {
      campaignRegistrationStore.markVoiceRegistrationPending()
    }

    if (whatsappPayload && !failedChannels.includes('WHATSAPP')) {
      await refreshWhatsAppChatsAfterCampaignRegister()
    }
    await refreshChatRegistration()
    notifyRegistrationOutcome(failedChannels)
    if (campaignAssignmentChanged.value) confirmCampaignAssignmentChange()
  } catch {
    // El backend registra por cola y persiste las que sí entraron antes de
    // fallar: se vuelve a preguntar en vez de asumir que no quedó nada.
    await refreshChatRegistration()
    showHeaderToast('error', t('call.error_register_queues'))
  } finally {
    loadingAction.value = null
  }
}

/**
 * El registro no es todo-o-nada: el backend responde 200 mientras al menos un
 * canal haya entrado, con los que fallaron en `failed_channels`. Se avisa qué
 * quedó fuera en vez de un "error al entrar a campaña" que contradiría al canal
 * que sí quedó registrado.
 */
const notifyRegistrationOutcome = (failedChannels: OperatorSessionChannel[]) => {
  if (!failedChannels.length) {
    showHeaderToast('success', t('call.success_register_queues'))
    return
  }

  showHeaderToast(
    'warn',
    t(
      failedChannels.includes('CALL')
        ? 'call.partial_registration_voice_failed'
        : 'call.partial_registration_chat_failed',
    ),
  )
}

/**
 * Re-registra SOLO el canal de chat, sin tocar la voz: el backend registra CALL
 * y luego WHATSAPP, así que un fallo en el segundo deja al agente dentro de las
 * colas y fuera de sus campañas de WhatsApp.
 */
const retryChatRegistration = async () => {
  if (!authStore.userId) {
    showHeaderToast('warn', t('call.missing_user'))
    return
  }

  const whatsappPayload = getRegisterWhatsappPayload()
  if (!whatsappPayload) {
    showHeaderToast('warn', t('call.no_campaign_channels_available'))
    return
  }

  loadingAction.value = 'register'
  try {
    await callService.registerOperatorSession(
      { user_id: authStore.userId, whatsapp: { campaign_ids: whatsappPayload.campaign_ids } },
      { notify: false },
    )
    // A diferencia de la voz, acá no hay heartbeat que confirme: campaign_logins
    // es la fuente y se puede leer de inmediato.
    await refreshWhatsAppChatsAfterCampaignRegister()
    await refreshChatRegistration()
    showHeaderToast('success', t('call.success_register_queues'))
  } catch {
    await refreshChatRegistration()
    showHeaderToast('error', t('call.error_register_queues'))
  } finally {
    loadingAction.value = null
  }
}

/**
 * Re-registra SOLO el canal de voz, sin tocar el chat: se usa cuando Asterisk
 * dejó de reportar al agente pero su campaign_login sigue abierto, estado en el
 * que "Iniciar campaña" no aparece porque el chat lo sigue dando por registrado.
 */
const retryVoiceRegistration = async () => {
  if (!authStore.userId) {
    showHeaderToast('warn', t('call.missing_user'))
    return
  }

  // Sin fallback a WhatsApp: acá el chat ya está registrado, así que los avisos
  // deben hablar de lo que falta (extensión, sesión, micrófono).
  const canRegisterVoice = await validateVoiceCampaignPrerequisites(false)
  if (!canRegisterVoice) return

  const callPayload = queueCampaigns.value.length ? getOperatorSessionCallPayload() : null
  if (!callPayload) {
    showHeaderToast('warn', t('call.no_campaign_channels_available'))
    return
  }

  loadingAction.value = 'register'
  try {
    await callService.registerOperatorSession(
      { user_id: authStore.userId, call: callPayload },
      { notify: false },
    )
    // No se sincroniza el flag de IA: ya se resolvió en el registro original y
    // recalcularlo solo con las campañas de voz pisaría el valor de las de chat.
    campaignRegistrationStore.markVoiceRegistrationPending()
    showHeaderToast('success', t('call.success_register_queues'))
  } catch {
    showHeaderToast('error', t('call.error_register_queues'))
  } finally {
    loadingAction.value = null
  }
}

const executeCampaignExit = async (isScheduledExit = false): Promise<boolean> => {
  if (hasActiveWebphoneCall.value) {
    if (!isScheduledExit) confirmScheduledCampaignExitVisible.value = true
    return false
  }

  if (isPaused.value) {
    confirmExitPauseSource.value = 'campaign'
    confirmExitPauseVisible.value = true
    if (isScheduledExit) campaignRegistrationStore.resolveScheduledCampaignExit(false)
    return false
  }

  if (!authStore.userId) {
    showHeaderToast('warn', t('call.missing_user'))
    if (isScheduledExit) campaignRegistrationStore.resolveScheduledCampaignExit(false)
    return false
  }

  const callPayload = queueCampaigns.value.length ? getOperatorSessionCallUnregisterPayload() : null
  const whatsappPayload = getUnregisterWhatsappPayload()

  loadingAction.value = 'unregister'
  let campaignExitCompleted = false
  try {
    const payload: IUnregisterOperatorSession = {
      user_id: authStore.userId,
      ...(callPayload ? { call: callPayload } : {}),
      ...(whatsappPayload ? { whatsapp: { enabled: true } } : {}),
    }

    await callService.unregisterOperatorSession(payload, { notify: false })
    clearRegisteredCampaignUseIa(
      getRegisteredCampaignIds(callPayload, whatsappPayload ? whatsappCampaignIds.value : []),
    )

    // La baja de voz la confirma la ausencia del evento: si el QueueRemove no
    // prosperó en Asterisk el agente sigue en la cola y el heartbeat lo va a
    // seguir reportando como registrado, que es la verdad.
    campaignRegistrationStore.expectVoiceUnregistration()
    pauseStatusStore.clearPause()
    await refreshChatRegistration()
    campaignExitCompleted = true
    showHeaderToast('success', t('call.success_unregister_queues'))
    return true
  } catch {
    await refreshChatRegistration()
    showHeaderToast('error', t('call.error_unregister_queues'))
    return false
  } finally {
    loadingAction.value = null
    if (isScheduledExit) {
      campaignRegistrationStore.resolveScheduledCampaignExit(campaignExitCompleted)
    }
  }
}

const exitCampaign = () => executeCampaignExit()

const reRegisterCampaign = async () => {
  if (loadingAction.value) return
  const exited = await executeCampaignExit()
  if (exited) {
    await enterCampaign()
  }
}

const exitCampaignAndLogout = async () => {
  shouldLogoutAfterScheduledCampaignExit.value = hasActiveWebphoneCall.value
  const campaignExitCompleted = await executeCampaignExit()
  if (!campaignExitCompleted) {
    if (!hasActiveWebphoneCall.value) shouldLogoutAfterScheduledCampaignExit.value = false
    return
  }

  shouldLogoutAfterScheduledCampaignExit.value = false
  await acceptLogout()
}

const toggleCampaignExitMenu = (event: MouseEvent) => {
  campaignExitMenu.value?.toggle(event)
}

watch(
  [hasActiveWebphoneCall, canProcessScheduledCampaignExit],
  ([hasActiveCall, canProcessExit]) => {
    if (hasActiveCall || !canProcessExit || !campaignRegistrationStore.claimScheduledCampaignExit())
      return

    void executeCampaignExit(true).then((campaignExitCompleted) => {
      if (!campaignExitCompleted || !shouldLogoutAfterScheduledCampaignExit.value) return

      shouldLogoutAfterScheduledCampaignExit.value = false
      void acceptLogout()
    })
  },
  { flush: 'post' },
)

watch(isRegisteredInCampaign, (isRegistered) => {
  if (isRegistered || !shouldLogoutAfterScheduledCampaignExit.value || isLogoutLoading.value) return

  shouldLogoutAfterScheduledCampaignExit.value = false
  void acceptLogout()
})

const getActivePause = async (): Promise<ActivePauseSelection | null> => {
  if (selectedPause.value) return selectedPause.value

  // Solo persiste el id entre recargas: se resuelve contra el catálogo.
  if (!pauses.value.length) {
    await loadPauses()
  }

  return selectedPause.value
}

const setQueuePause = async (pause: ActivePauseSelection, pauseState: boolean) => {
  if (!authStore.userId) {
    showHeaderToast('warn', t('call.missing_user'))
    return
  }

  if (pauseState) {
    try {
      await confirmPauseCreation()
    } catch {
      return
    }
  }

  loadingAction.value = 'pause'
  try {
    const result = await callService.pauseQueues({
      user_id: authStore.userId,
      pause: {
        id: pause.id,
        name: pause.name,
        description: pause.description,
      },
      is_paused: pauseState,
      extension: authStore.userExtension || extensionNumber.value,
      protocol: 'PJSIP',
    })

    if (!result.status) return

    if (pauseState) {
      pauseStatusStore.applySelfPause(pause)
    } else {
      pauseStatusStore.clearPause()
    }
  } finally {
    loadingAction.value = null
  }
}

const exitPause = async () => {
  // Único motivo de bloqueo: el origen de la pausa, que sale del PausedReason que
  // reporta Asterisk. Antes se bloqueaba por no tener la pausa en memoria, y eso
  // también pasa con pausas propias (recarga, otro navegador, POST que no volvió),
  // así que el agente no podía reanudar la suya.
  if (isSupervisorPause.value) {
    showHeaderToast('warn', t('call.supervisor_pause_locked'))
    return
  }

  // Reanudar no necesita la pausa de catálogo: el backend cierra el pause_log
  // abierto del usuario sin mirar el id y manda Reason vacío a Asterisk. Se envía
  // igual aunque no se haya podido resolver cuál era —el botón solo se pulsa
  // estando pausado y despausar es idempotente—: quedarse sin mandarlo dejaba al
  // agente pausado en Asterisk sin salida.
  const activePause = await getActivePause()
  await setQueuePause(activePause ?? { id: '', name: '', description: '' }, false)
}

const togglePauseMenu = async (event: MouseEvent) => {
  // Pausar exige estar en campaña; salir de una pausa abierta, no: sin registro
  // el agente no tendría forma de cerrarla.
  if (!isRegisteredInCampaign.value && !isPaused.value) return

  if (isPaused.value) {
    await exitPause()
    return
  }

  if (!pauses.value.length) {
    await loadPauses()
  }

  pauseMenu.value?.toggle(event)
}

const pauseMenuItems = computed<MenuItem[]>(() => {
  const items: MenuItem[] = pauses.value.map((pause) => ({
    label: pause.name,
    command: () => void setQueuePause(pause, true),
  }))

  if (!items.length) {
    return [{ label: t('call.no_pauses_available'), disabled: true }]
  }

  return items
})

const campaignExitMenuItems = computed<CampaignExitMenuItem[]>(() => [
  {
    label: t('header.actions.end_campaign'),
    iconComponent: IconExitArrowCircle,
    command: () => void exitCampaign(),
  },
  {
    label: t('header.actions.end_campaign_and_logout'),
    iconComponent: LogOut,
    command: () => void exitCampaignAndLogout(),
  },
])

const confirmExitPauseTitle = computed(() =>
  confirmExitPauseSource.value === 'campaign'
    ? t('call.confirm_exit_pause_campaign_title')
    : t('call.confirm_exit_pause_title'),
)

const confirmExitPauseMessage = computed(() =>
  confirmExitPauseSource.value === 'campaign'
    ? t('call.confirm_exit_pause_from_campaign_message')
    : t('call.confirm_exit_pause_message'),
)

const confirmScheduledCampaignExitTitle = computed(() =>
  shouldLogoutAfterScheduledCampaignExit.value
    ? t('call.confirm_scheduled_campaign_exit_logout_title')
    : t('call.confirm_scheduled_campaign_exit_title'),
)

const confirmScheduledCampaignExitMessage = computed(() =>
  shouldLogoutAfterScheduledCampaignExit.value
    ? t('call.confirm_scheduled_campaign_exit_logout_message')
    : t('call.confirm_scheduled_campaign_exit_message'),
)

const scheduledCampaignExitToastMessage = computed(() =>
  shouldLogoutAfterScheduledCampaignExit.value
    ? t('call.campaign_exit_logout_scheduled')
    : t('call.campaign_exit_scheduled'),
)

const cancelExitPause = () => {
  confirmExitPauseVisible.value = false
}

const cancelScheduledCampaignExit = () => {
  shouldLogoutAfterScheduledCampaignExit.value = false
  confirmScheduledCampaignExitVisible.value = false
}

const confirmScheduledCampaignExit = () => {
  campaignRegistrationStore.scheduleCampaignExit(isClassificationFormActive.value)
  confirmScheduledCampaignExitVisible.value = false
  showHeaderToast('info', scheduledCampaignExitToastMessage.value)
}

const confirmExitPause = async () => {
  await exitPause()
  confirmExitPauseVisible.value = false
}

onMounted(() => {
  refreshWebphonePermissions()
  void loadCampaigns()
  void loadPauses()
  void refreshChatRegistration()
})

const buttonActions = computed<ButtonActions[]>(() => {
  if (!isRegisteredInCampaign.value) {
    const startCampaignAction: ButtonActions = {
      icon: IconIncomingArrowCircle,
      text: t('header.actions.start_campaign'),
      textIsVisible: 1,
      action: enterCampaign,
      condition: null,
      color: 'var(--p-primary-color)',
      disabled: isCampaignStatusLoading.value || loadingAction.value === 'register',
      loading: isCampaignStatusLoading.value || loadingAction.value === 'register',
    }
    if (!isPaused.value) return [startCampaignAction]

    // Una pausa abierta sin registro en campaña: sin este botón no hay forma de
    // cerrarla desde el Oper.
    return [
      {
        icon: IconWaitingTime,
        text: t('call.exit_pause'),
        textIsVisible: 1,
        action: togglePauseMenu,
        condition: null,
        color: 'var(--p-amber-500, #f59e0b)',
        disabled: loadingAction.value === 'pause',
        loading: loadingAction.value === 'pause',
      },
      startCampaignAction,
    ]
  }

  return [
    {
      icon: IconWaitingTime,
      text: isPaused.value ? t('call.exit_pause') : t('header.actions.pause'),
      textIsVisible: isPaused.value ? 1 : 0,
      action: togglePauseMenu,
      condition: null,
      color: isPaused.value ? 'var(--p-amber-500, #f59e0b)' : 'var(--p-primary-color)',
      disabled: isCampaignStatusLoading.value || loadingAction.value === 'pause',
      loading: loadingAction.value === 'pause',
    },
    {
      icon: IconExitArrowCircle,
      text: t('header.actions.end_campaign'),
      textIsVisible: 0,
      action: toggleCampaignExitMenu,
      condition: null,
      color: 'var(--p-surface-500)',
      disabled:
        isCampaignStatusLoading.value ||
        loadingAction.value === 'unregister' ||
        isLogoutLoading.value,
      loading: loadingAction.value === 'unregister' || isLogoutLoading.value,
    },
  ]
})
</script>

<template>
  <div class="hidden lg:block m-3">
    <div
      class="app-card-glass shadow-sm flex flex-row justify-between items-center px-3.5 py-2.5 rounded-lg transition-all duration-200"
    >
      <!-- Left: Session unit (Webphone + Extension info + Date) + Agent Status Pill -->
      <div class="flex items-center gap-3">
        <div class="flex items-center gap-2.5 px-2.5 py-1 transition-all duration-200">
          <div class="flex items-center gap-1.5">
            <!-- Webphone button -->
            <button
              type="button"
              v-tooltip.bottom="webphoneButtonTooltip"
              class="flex items-center gap-1.5 rounded-md px-1.5 py-1 transition-all duration-200 active:scale-95"
              :class="[
                isWebphoneInteractionDisabled
                  ? 'cursor-not-allowed opacity-50'
                  : hasActiveWebphoneCall
                    ? 'cursor-pointer hover:bg-rose-500/10'
                    : 'cursor-pointer hover:bg-[var(--p-primary-color)]/10',
              ]"
              :disabled="isWebphoneInteractionDisabled"
              @click="handleWebphoneClick"
            >
              <IconPhone
                class="h-6 w-6"
                :stroke-width="2"
                :class="
                  shouldShowWebphoneDisconnectedStyle
                    ? 'text-[var(--p-surface-400)] dark:text-[var(--p-surface-200)]'
                    : hasActiveWebphoneCall
                      ? 'text-red-500 dark:text-red-400'
                      : 'text-[var(--p-primary-color)]'
                "
              />
              <span
                class="montserrat text-[11px] font-bold tabular-nums leading-none"
                :class="
                  shouldShowWebphoneDisconnectedStyle
                    ? 'text-[var(--p-surface-400)] dark:text-[var(--p-surface-200)]'
                    : hasActiveWebphoneCall
                      ? 'text-red-500 dark:text-red-400'
                      : 'text-[var(--p-primary-color)]'
                "
              >
                {{ extensionNumber }}
              </span>
            </button>

            <button
              v-if="shouldShowWebphoneInfo"
              type="button"
              class="flex h-5 w-5 items-center justify-center rounded-full text-amber-500 transition hover:bg-amber-50 dark:hover:bg-amber-400/10"
              v-tooltip.bottom="webphoneInfoTooltip"
              @mouseenter="refreshWebphonePermissions"
              @focus="refreshWebphonePermissions"
            >
              <IconInfo class="h-4 w-4" />
            </button>
          </div>

          <div class="h-5 w-px bg-[var(--p-surface-200)] dark:bg-[var(--p-surface-700)]" />

          <div class="flex flex-col leading-tight min-w-0">
            <span
              class="montserrat text-xs font-semibold text-[var(--p-surface-900)] dark:text-[var(--p-surface-0)] truncate"
            >
              {{ authStore.user?.name || extensionNameWithStatus }}
            </span>
            <div class="flex items-center gap-1.5 text-[10px] text-[var(--p-surface-500)] dark:text-[var(--p-surface-0)]/90 truncate">
              <span v-if="extensionDescription && extensionDescription !== '-'" class="truncate max-w-[130px]">
                {{ extensionDescription }}
              </span>
              <span v-if="extensionDescription && extensionDescription !== '-'">·</span>
              <span class="capitalize truncate">{{ currentDate }}</span>
            </div>
          </div>
        </div>

        <!-- Operational Status Pill -->
        <div
          v-if="agentOperationalState"
          class="inline-flex items-center gap-2 px-2 xl:px-3 py-0.5 rounded-full text-xs font-semibold border tabular-nums transition-all duration-150 shrink-0"
          :class="agentOperationalState.badgeClass"
        >
          <span class="w-2 h-2 rounded-full shrink-0" :class="agentOperationalState.dotClass"></span>
          <WifiOff
            v-if="agentOperationalState.type === 'disconnected'"
            class="h-3.5 w-3.5 xl:hidden"
            :aria-label="agentOperationalState.label"
            :title="agentOperationalState.label"
          />
          <span class="hidden xl:inline">{{ agentOperationalState.label }}</span>
        </div>

        <!-- Campaign Registration Status Pill -->
        <div
          v-if="campaignRegistrationState"
          class="inline-flex items-center gap-2 px-2 xl:px-3 py-0.5 rounded-full text-xs font-semibold border tabular-nums transition-all duration-150 shrink-0"
          :class="campaignRegistrationState.badgeClass"
        >
          <span class="w-2 h-2 rounded-full shrink-0" :class="campaignRegistrationState.dotClass"></span>
          <span v-if="campaignRegistrationState.type === 'registered' && campaignRegistrationState.count" class="xl:hidden">{{ campaignRegistrationState.count }}</span>
          <span class="hidden xl:inline">{{ campaignRegistrationState.label }}</span>
        </div>
      </div>

      <div class="flex items-center gap-3 mr-1">
        <!-- Main Operative Action Buttons -->
        <div class="flex items-center gap-2">
          <div v-for="(buttonAction, index) in buttonActions" :key="index">
            <AppButton
              v-tooltip.bottom="buttonAction.text"
              severity="secondary"
              size="small"
              outlined
              rounded
              :block="false"
              :disabled="buttonAction.disabled"
              :loading="buttonAction.loading"
              :style="{
                color: buttonAction.disabled ? '#B2B2B2' : buttonAction.color,
                borderColor: buttonAction.disabled ? '#D1D5DB' : buttonAction.color,
              }"
              class="shadow-xs min-h-9 text-xs! transition-all duration-150 dark:text-[var(--p-primary-50)]/90! dark:border-[var(--p-primary-50)]/90! hover:opacity-90 active:scale-95 px-3 py-1.5 rounded-lg flex items-center gap-1.5"
              :class="[
                !isRegisteredInCampaign && index === 0 ? '!bg-[var(--p-primary-color)] !text-white !border-[var(--p-primary-color)] font-semibold shadow-sm' : '',
                isPaused && index === 0 ? '!bg-amber-500 !text-white !border-amber-500 font-semibold shadow-sm hover:!bg-amber-600' : ''
              ]"
              @click="buttonAction.action"
            >
              <template #icon-start>
                <component :is="buttonAction.icon" class="w-4 h-4" :stroke-width="1.5" />
                <div v-if="buttonAction.textIsVisible" class="font-medium">{{ buttonAction.text }}</div>
              </template>
            </AppButton>
          </div>
        </div>

        <Menu
          ref="pauseMenu"
          :model="pauseMenuItems"
          :popup="true"
          class="!rounded-lg text-sm !border !border-[var(--p-surface-200)]/80 dark:!border-[var(--p-primary-400)]/40 !bg-[var(--p-surface-0)] dark:!bg-[var(--p-primary-950)]/70 backdrop-blur-2xl !backdrop-saturate-150 !shadow-lg [&_.p-menu-list]:!bg-transparent [&_.p-menu-item-content]:!bg-transparent [&_.p-menu-item-content:hover]:!bg-[var(--p-primary-color)]/12 [&_.p-menu-item-icon]:!text-[var(--p-text-color)] dark:[&_.p-menu-item-icon]:!text-[var(--p-surface-100)] [&_.p-menu-item-label]:!text-[var(--p-text-color)] dark:[&_.p-menu-item-label]:!text-[var(--p-surface-100)]"
        />
        <Menu
          ref="campaignExitMenu"
          :model="campaignExitMenuItems"
          :popup="true"
          class="!rounded-lg text-sm !border !border-[var(--p-surface-200)]/80 dark:!border-[var(--p-primary-400)]/40 !bg-[var(--p-surface-0)] dark:!bg-[var(--p-primary-950)]/70 backdrop-blur-2xl !backdrop-saturate-150 !shadow-lg [&_.p-menu-list]:!bg-transparent [&_.p-menu-item-content]:!bg-transparent [&_.p-menu-item-content:hover]:!bg-[var(--p-primary-color)]/12 [&_.p-menu-item-icon]:!text-[var(--p-text-color)] dark:[&_.p-menu-item-icon]:!text-[var(--p-surface-100)] [&_.p-menu-item-label]:!text-[var(--p-text-color)] dark:[&_.p-menu-item-label]:!text-[var(--p-surface-100)]"
        >
          <template #item="{ item, props }">
            <a
              v-bind="props.action"
              class="flex items-center gap-2 text-[var(--p-text-color)] dark:text-[var(--p-surface-100)]"
            >
              <component :is="item.iconComponent" class="h-4 w-4" :stroke-width="1.5" />
              <span class="text-[var(--p-text-color)] dark:text-[var(--p-primary-50)]">{{ item.label }}</span>
            </a>
          </template>
        </Menu>

        <!-- Secondary Utilities Subgroup (demoted prominence) -->
        <div class="flex items-center gap-1.5 pl-2 border-l border-[var(--p-surface-200)]/80 dark:border-[var(--p-surface-800)]">
          <AppNotificationSettings />
          <AppThemeSwitcher />
          <AppDarkMode />
        </div>
      </div>
    </div>

    <!-- Notificación refinada de registro en campaña -->
    <div
      v-if="!isRegisteredInCampaign && !isCampaignAlertDismissed"
      class="mt-1.5 flex items-center justify-between gap-3 rounded-lg border border-amber-400/35 bg-amber-500/10 px-3.5 py-1.5 text-xs transition-all duration-200 dark:border-amber-500/25 dark:bg-amber-500/10"
      role="status"
    >
      <div class="flex flex-wrap items-center gap-2 sm:flex-nowrap">
        <span class="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-500/20 text-amber-700 dark:bg-amber-400/20 dark:text-amber-300">
          <IconInfo class="h-3.5 w-3.5" />
        </span>
        <span class="font-semibold text-amber-950 dark:text-amber-100">
          {{ t('call.not_registered_campaign_title') }}
        </span>
        <span class="hidden sm:inline text-amber-700/60 dark:text-amber-400/40">•</span>
        <span class="text-amber-900/80 dark:text-amber-200/80">
          {{ t('call.campaign_registration_prompt_before') }}<button
            type="button"
            class="cursor-pointer font-medium text-amber-600 underline transition hover:text-amber-700 dark:text-amber-100 dark:hover:text-amber-300"
            :disabled="isCampaignStatusLoading || loadingAction === 'register'"
            @click="enterCampaign"
          >{{ t('call.campaign_registration_prompt_link') }}</button>{{ t('call.campaign_registration_prompt_after') }}
        </span>
      </div>

      <div class="flex shrink-0 items-center gap-2">
        <!-- <button
          type="button"
          class="cursor-pointer inline-flex items-center justify-center rounded-md bg-[var(--p-primary-color)] px-2.5 py-1 text-xs font-semibold text-white shadow-xs transition hover:opacity-90 active:scale-95 disabled:cursor-wait disabled:opacity-60"
          :disabled="isCampaignStatusLoading || loadingAction === 'register'"
          @click="enterCampaign"
        >
          {{ t('call.register_now') }}
        </button> -->
        <button
          type="button"
          class="cursor-pointer flex h-6 w-6 items-center justify-center rounded-md text-amber-800/70 transition hover:bg-amber-500/20 hover:text-amber-950 dark:text-amber-300/70 dark:hover:bg-amber-500/20 dark:hover:text-amber-100"
          :aria-label="t('call.dismiss_notification')"
          @click="isCampaignAlertDismissed = true"
        >
          <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
    </div>

    <div
      v-if="campaignAssignmentChanged"
      class="mt-1 flex min-h-7 flex-wrap items-center justify-center gap-x-1.5 rounded-md border border-[#F59E0B]/30 bg-[#F59E0B]/10 px-3 py-1 text-center text-xs font-semibold text-[#B45309] dark:border-[#F59E0B]/40 dark:bg-[#F59E0B]/15 dark:text-[#FBBF24]"
    >
      <span class="font-bold">{{ t('call.campaign_assignment_changed_title') }}.</span>
      <span>{{ t('call.campaign_assignment_changed_desc') }}</span>
      <button
        type="button"
        class="cursor-pointer font-semibold underline underline-offset-2 transition hover:text-[#92400E] dark:hover:text-[#FCD34D]"
        @click="reRegisterCampaign"
      >
        {{ t('call.exit_and_register_cta') }}
      </button>
    </div>

    <div
      v-if="isVoiceRegistrationLost && !isVoiceRegistrationAlertDismissed"
      class="mt-1.5 flex items-center justify-between gap-3 rounded-lg border border-amber-400/35 bg-amber-500/10 px-3.5 py-1.5 text-xs transition-all duration-200 dark:border-amber-500/25 dark:bg-amber-500/10"
      role="status"
    >
      <div class="flex flex-wrap items-center gap-2 sm:flex-nowrap">
        <span class="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-500/20 text-amber-700 dark:bg-amber-400/20 dark:text-amber-300">
          <IconInfo class="h-3.5 w-3.5" />
        </span>
        <span class="font-semibold text-amber-950 dark:text-amber-100">{{ t('call.voice_registration_lost') }}</span>
      </div>
      <div class="flex shrink-0 items-center gap-2">
        <button
          type="button"
          class="cursor-pointer rounded-md bg-[var(--p-primary-color)] px-2.5 py-1 text-xs font-semibold text-white shadow-xs transition hover:opacity-90 active:scale-95 disabled:cursor-wait disabled:opacity-60"
          :disabled="loadingAction === 'register'"
          @click="retryVoiceRegistration"
        >
          {{ t('call.retry_voice_registration') }}
        </button>
        <button
          type="button"
          class="cursor-pointer flex h-6 w-6 items-center justify-center rounded-md text-amber-800/70 transition hover:bg-amber-500/20 hover:text-amber-950 dark:text-amber-300/70 dark:hover:bg-amber-500/20 dark:hover:text-amber-100"
          :aria-label="t('call.dismiss_notification')"
          @click="isVoiceRegistrationAlertDismissed = true"
        >
          <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
    </div>

    <div
      v-else-if="isChatRegistrationLost && !isChatRegistrationAlertDismissed"
      class="mt-1.5 flex items-center justify-between gap-3 rounded-lg border border-amber-400/35 bg-amber-500/10 px-3.5 py-1.5 text-xs transition-all duration-200 dark:border-amber-500/25 dark:bg-amber-500/10"
      role="status"
    >
      <div class="flex flex-wrap items-center gap-2 sm:flex-nowrap">
        <span class="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-500/20 text-amber-700 dark:bg-amber-400/20 dark:text-amber-300">
          <IconInfo class="h-3.5 w-3.5" />
        </span>
        <span class="font-semibold text-amber-950 dark:text-amber-100">{{ t('call.chat_registration_lost') }}</span>
      </div>
      <div class="flex shrink-0 items-center gap-2">
        <button
          type="button"
          class="cursor-pointer rounded-md bg-[var(--p-primary-color)] px-2.5 py-1 text-xs font-semibold text-white shadow-xs transition hover:opacity-90 active:scale-95 disabled:cursor-wait disabled:opacity-60"
          :disabled="loadingAction === 'register'"
          @click="retryChatRegistration"
        >
          {{ t('call.retry_chat_registration') }}
        </button>
        <button
          type="button"
          class="cursor-pointer flex h-6 w-6 items-center justify-center rounded-md text-amber-800/70 transition hover:bg-amber-500/20 hover:text-amber-950 dark:text-amber-300/70 dark:hover:bg-amber-500/20 dark:hover:text-amber-100"
          :aria-label="t('call.dismiss_notification')"
          @click="isChatRegistrationAlertDismissed = true"
        >
          <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
    </div>

    <Dialog
      v-model:visible="confirmExitPauseVisible"
      modal
      :header="confirmExitPauseTitle"
      :style="{ width: '28rem' }"
      :draggable="false"
    >
      <p class="m-0 text-sm leading-6 text-gray-700 dark:text-gray-200">
        {{ confirmExitPauseMessage }}
      </p>

      <template #footer>
        <div class="flex justify-end gap-2">
          <AppButton
            :label="t('general.cancel')"
            variant="secondary"
            outlined
            :block="false"
            @click="cancelExitPause"
          />
          <AppButton
            :label="t('call.exit_pause')"
            variant="danger"
            :block="false"
            :loading="loadingAction === 'pause'"
            @click="confirmExitPause"
          />
        </div>
      </template>
    </Dialog>

    <Dialog
      v-model:visible="confirmScheduledCampaignExitVisible"
      modal
      :header="confirmScheduledCampaignExitTitle"
      :style="{ width: '28rem' }"
      :draggable="false"
    >
      <p class="m-0 text-sm leading-6 text-gray-700 dark:text-gray-200">
        {{ confirmScheduledCampaignExitMessage }}
      </p>

      <template #footer>
        <div class="flex justify-end gap-2">
          <AppButton
            :label="t('general.cancel')"
            variant="secondary"
            outlined
            :block="false"
            @click="cancelScheduledCampaignExit"
          />
          <AppButton
            :label="t('call.confirm_scheduled_campaign_exit_continue')"
            variant="danger"
            :block="false"
            @click="confirmScheduledCampaignExit"
          />
        </div>
      </template>
    </Dialog>
  </div>
</template>

<style scoped>
@keyframes campaign-pulse {
  0%,
  100% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.025);
  }
}

.animate-campaign-pulse {
  animation: campaign-pulse 1.4s ease-in-out infinite;
  transform-origin: center;
}

/* .animate-campaign-pulse {
  animation: campaign-pulse 1.4s ease-in-out infinite;
  transform-origin: center;
  filter: drop-shadow(0 0 1px rgba(234, 58, 61, 0.25));
} */
</style>
